/**
 * 语音录制接线 hook（任务 14.2b-2）：getUserMedia + MediaRecorder → sendVoice。
 * 状态裁决全在 voiceRecorderModel（可单测），这里只做媒体流/计时器/提示条接线。
 *
 * - 无 MediaRecorder 或 getUserMedia（iOS 14.0~14.2、部分 X5）→ supported=false，
 *   调用方隐藏入口（docs/compat.md「语音录制」节，真机差异待任务 19）。
 * - 录出什么格式发什么：blob.type 即实录 MIME，Worker 端据此定 codec/文件名
 *   （voiceSend.ts 单点）；时长用状态机实测值（envelope dur_ms 口径）。
 * - 提示条 3s 自清（useImageSender 同模式）。
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { pickRecordingMime } from '~/chat/voiceSend';
import { messagesConnection } from '~/store/appWorker';
import {
  createVoiceRecorderMachine,
  recorderErrorText,
  voiceSendErrorText,
  type VoiceRecorderPhase,
} from './voiceRecorderModel';

const TIP_MS = 3_000;
const CLOCK_TICK_MS = 200;

export interface VoiceRecorderHandle {
  readonly supported: boolean;
  readonly phase: VoiceRecorderPhase;
  readonly elapsedMs: number;
  readonly cancelArmed: boolean;
  /** 当前提示文案（空串=无提示） */
  readonly tip: string;
  start(): void;
  /** 移动端按住期间的水平位移（相对按下点） */
  slide(dx: number): void;
  /** 松手/点击停止：按时长发送或丢弃 */
  stop(): void;
  cancel(): void;
}

interface RecorderView {
  phase: VoiceRecorderPhase;
  elapsedMs: number;
  cancelArmed: boolean;
}

const IDLE_VIEW: RecorderView = { phase: 'idle', elapsedMs: 0, cancelArmed: false };

export function useVoiceRecorder(): VoiceRecorderHandle {
  const supported = useMemo(
    () =>
      typeof MediaRecorder !== 'undefined' &&
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.getUserMedia === 'function',
    [],
  );
  const machine = useMemo(() => createVoiceRecorderMachine(() => Date.now()), []);
  const [view, setView] = useState<RecorderView>(IDLE_VIEW);
  const [tip, setTip] = useState('');
  const tipTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const recorderRef = useRef<MediaRecorder | null>(null);
  /** stop() 裁决为发送时记下实录时长，onstop 组装 blob 后消费 */
  const pendingSendMsRef = useRef<number | null>(null);

  const syncView = useCallback(() => {
    setView(
      machine.phase === 'idle'
        ? IDLE_VIEW
        : { phase: machine.phase, elapsedMs: machine.elapsedMs(), cancelArmed: machine.cancelArmed() },
    );
  }, [machine]);

  const showTip = useCallback((text: string) => {
    setTip(text);
    if (tipTimerRef.current !== undefined) clearTimeout(tipTimerRef.current);
    tipTimerRef.current = setTimeout(() => setTip(''), TIP_MS);
  }, []);

  // 录制中按帧率无关的粗粒度计时刷新（时长标签精度到秒，200ms 足够）
  useEffect(() => {
    if (view.phase !== 'recording') return undefined;
    const timer = setInterval(syncView, CLOCK_TICK_MS);
    return () => clearInterval(timer);
  }, [view.phase, syncView]);

  // 卸载（切会话 remount）：丢弃进行中的录制并释放媒体流，不发送
  useEffect(
    () => () => {
      machine.cancel();
      pendingSendMsRef.current = null;
      const rec = recorderRef.current;
      recorderRef.current = null;
      if (rec && rec.state !== 'inactive') rec.stop();
      if (tipTimerRef.current !== undefined) clearTimeout(tipTimerRef.current);
    },
    [machine],
  );

  const start = useCallback(() => {
    if (!supported) return;
    if (machine.start() !== 'request-media') return;
    syncView();
    navigator.mediaDevices.getUserMedia({ audio: true }).then(
      (stream) => {
        const releaseStream = () => stream.getTracks().forEach((t) => t.stop());
        if (machine.mediaReady() !== 'record') {
          releaseStream(); // 权限未决期间已松手/取消
          return;
        }
        const mime = pickRecordingMime((t) => MediaRecorder.isTypeSupported(t));
        let rec: MediaRecorder;
        try {
          rec = mime === '' ? new MediaRecorder(stream) : new MediaRecorder(stream, { mimeType: mime });
        } catch {
          releaseStream();
          machine.cancel();
          syncView();
          showTip('当前设备不支持语音录制');
          return;
        }
        // chunks 持在本录制的闭包里：旧录制 onstop 晚到也不会与新录制交错
        const chunks: Blob[] = [];
        rec.ondataavailable = (e) => {
          if (e.data.size > 0) chunks.push(e.data);
        };
        rec.onstop = () => {
          releaseStream();
          const durationMs = pendingSendMsRef.current;
          pendingSendMsRef.current = null;
          const type = rec.mimeType !== '' ? rec.mimeType : mime;
          const blob = new Blob(chunks, type === '' ? undefined : { type });
          if (durationMs === null) return; // 取消/太短：丢弃
          if (blob.size === 0) {
            showTip('语音发送失败，请重试');
            return;
          }
          void messagesConnection()
            .sendVoice(blob, durationMs)
            .then(
              (outcome) => {
                const text = voiceSendErrorText(outcome);
                if (text !== '') showTip(text);
              },
              () => showTip('语音发送失败，请重试'),
            );
        };
        recorderRef.current = rec;
        rec.start();
        syncView();
      },
      (err: unknown) => {
        machine.mediaFailed();
        syncView();
        showTip(recorderErrorText(err));
      },
    );
  }, [supported, machine, syncView, showTip]);

  const finishWith = useCallback(
    (mode: 'stop' | 'cancel') => {
      if (mode === 'cancel') {
        machine.cancel();
        pendingSendMsRef.current = null;
      } else {
        const res = machine.stop();
        pendingSendMsRef.current = res.kind === 'send' ? res.durationMs : null;
        if (res.kind === 'too-short') showTip('录音时间太短');
      }
      syncView();
      const rec = recorderRef.current;
      recorderRef.current = null;
      if (rec && rec.state !== 'inactive') rec.stop();
    },
    [machine, syncView, showTip],
  );

  return {
    supported,
    phase: view.phase,
    elapsedMs: view.elapsedMs,
    cancelArmed: view.cancelArmed,
    tip,
    start,
    slide: useCallback(
      (dx: number) => {
        machine.slide(dx);
        syncView();
      },
      [machine, syncView],
    ),
    stop: useCallback(() => finishWith('stop'), [finishWith]),
    cancel: useCallback(() => finishWith('cancel'), [finishWith]),
  };
}
